import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useEdit } from "../../lib/content";
import { API, fetchGoldRates } from "../../lib/store";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";

const CATS = ["gold", "diamond", "silver", "wedding-bands", "bullion", "lladro"];
const KARATS = [["24", "24K"], ["22", "22K"], ["18", "18K"]];

export function autoPrice(product, gold) {
  if (product?.priceMode === "auto" && product.gramWeight > 0 && gold?.perGram) {
    const k = product.karat === "24" ? "k24" : product.karat === "18" ? "k18" : "k22";
    return Math.round(product.gramWeight * gold.perGram[k]);
  }
  return null;
}

export function ProductEditModal({ open, onClose, product, onSaved }) {
  const { authHeader } = useEdit();
  const [f, setF] = useState({
    title: product.title || "",
    description: product.description || "",
    category: product.categories?.[0] || "",
    tags: (product.tags || []).join(", "),
    available: product.available !== false,
    hidden: Boolean(product.hidden),
    priceMode: product.priceMode || "fixed",
    price: product.price || 0,
    gramWeight: product.gramWeight || "",
    karat: product.karat || "22",
  });
  const [gold, setGold] = useState(null);
  const [busy, setBusy] = useState(false);
  const upd = (patch) => setF((p) => ({ ...p, ...patch }));

  React.useEffect(() => { if (open) fetchGoldRates().then(setGold).catch(() => {}); }, [open]);

  const preview = f.priceMode === "auto" && f.gramWeight > 0 && gold
    ? Math.round(Number(f.gramWeight) * gold.perGram[f.karat === "24" ? "k24" : f.karat === "18" ? "k18" : "k22"])
    : null;

  const save = async () => {
    setBusy(true);
    const body = {
      title: f.title,
      description: f.description,
      category: f.category || null,
      tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
      available: f.available,
      hidden: f.hidden,
      priceMode: f.priceMode,
      karat: f.karat,
      gramWeight: f.gramWeight ? Number(f.gramWeight) : 0,
    };
    if (f.priceMode === "fixed") body.price = Number(f.price) || 0;
    try {
      await axios.patch(`${API}/admin/inventory/products/${product.handle}`, body, { headers: authHeader() });
      // reflect changes locally so the page updates without a full reload
      Object.assign(product, {
        title: body.title, description: body.description, tags: body.tags,
        available: body.available, hidden: body.hidden, priceMode: body.priceMode,
        karat: body.karat, gramWeight: body.gramWeight,
        categories: body.category ? [body.category] : product.categories,
        ...(f.priceMode === "fixed" ? { price: body.price } : {}),
      });
      toast.success("Product updated — live now");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border-[#d8cdb5] max-w-lg max-h-[90vh] overflow-auto" data-testid="product-edit-modal">
        <DialogHeader><DialogTitle className="font-heading text-xl">Edit Product</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Edit product details and pricing. Auto pricing follows the live gold rate.</DialogDescription>
        <div className="space-y-3">
          <div><Label className="text-xs uppercase tracking-wider text-[#7a7160]">Name</Label>
            <Input value={f.title} onChange={(e) => upd({ title: e.target.value })} className="rounded-none border-[#d8cdb5] h-10" data-testid="pe-title" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-[#7a7160]">Description</Label>
            <Textarea value={f.description} onChange={(e) => upd({ description: e.target.value })} rows={3} className="rounded-none border-[#d8cdb5]" data-testid="pe-desc" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider text-[#7a7160]">Category</Label>
              <select value={f.category} onChange={(e) => upd({ category: e.target.value })} className="w-full rounded-none border border-[#d8cdb5] h-10 px-2 bg-white text-sm" data-testid="pe-category">
                <option value="">—</option>
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></div>
            <div><Label className="text-xs uppercase tracking-wider text-[#7a7160]">Tags (comma-separated)</Label>
              <Input value={f.tags} onChange={(e) => upd({ tags: e.target.value })} placeholder="22kt, chain, men" className="rounded-none border-[#d8cdb5] h-10" data-testid="pe-tags" /></div>
          </div>

          {/* Pricing — guardrail: auto = weight x live rate, no price box */}
          <div className="border border-[#eee6d6] p-3 bg-[#faf7f0]">
            <Label className="text-xs uppercase tracking-wider text-[#a8842c]">Pricing</Label>
            <div className="flex gap-4 mt-2 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="priceMode" checked={f.priceMode === "fixed"} onChange={() => upd({ priceMode: "fixed" })} data-testid="pe-mode-fixed" /> Fixed price
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="priceMode" checked={f.priceMode === "auto"} onChange={() => upd({ priceMode: "auto" })} data-testid="pe-mode-auto" /> Auto (weight × live gold rate)
              </label>
            </div>
            {f.priceMode === "fixed" ? (
              <div className="mt-3">
                <Label className="text-[11px] text-[#8f8571]">Price (USD)</Label>
                <Input type="number" value={f.price} onChange={(e) => upd({ price: e.target.value })} className="rounded-none border-[#d8cdb5] h-10" data-testid="pe-price" />
              </div>
            ) : (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div><Label className="text-[11px] text-[#8f8571]">Gram weight</Label>
                  <Input type="number" value={f.gramWeight} onChange={(e) => upd({ gramWeight: e.target.value })} className="rounded-none border-[#d8cdb5] h-10" data-testid="pe-weight" /></div>
                <div><Label className="text-[11px] text-[#8f8571]">Karat</Label>
                  <select value={f.karat} onChange={(e) => upd({ karat: e.target.value })} className="w-full rounded-none border border-[#d8cdb5] h-10 px-2 bg-white text-sm" data-testid="pe-karat">
                    {KARATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select></div>
                <p className="col-span-2 text-xs text-[#5c5240]">
                  {preview != null ? <>Live price today: <strong>${preview.toLocaleString()}</strong> (updates automatically with the gold rate)</> : "Enter a weight to preview today's live price."}
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.available} onCheckedChange={(v) => upd({ available: v })} data-testid="pe-available" /> Available</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.hidden} onCheckedChange={(v) => upd({ hidden: v })} data-testid="pe-hidden" /> Hidden from site</label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="rounded-none border-[#d8cdb5] text-xs">Cancel</Button>
            <Button onClick={save} disabled={busy} className="rounded-none bg-[#171310] text-[#e9d9ac] text-xs px-6" data-testid="pe-save">{busy ? "Saving…" : "Save"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
